<template>
  <component
    :is="resolvedTag"
    class="play-dropdown-item"
    :class="[`play-dropdown-item--${variant}`, { 'is-active': active, 'is-disabled': disabled }]"
    role="menuitem"
    tabindex="-1"
    :href="href || null"
    :to="to || null"
    :type="resolvedTag === 'button' ? 'button' : null"
    :disabled="resolvedTag === 'button' ? disabled : null"
    :aria-disabled="disabled ? 'true' : null"
    v-bind="$attrs"
    @click="onClick"
  >
    <span
      v-if="$slots.icon"
      class="play-dropdown-item__icon"
    ><slot name="icon" /></span>
    <span class="play-dropdown-item__label"><slot /></span>
  </component>
</template>

<script>
/**
 * PlayDropdownItem — a single option inside <PlayDropdown>.
 *
 * Token-driven (see src/styles/tokens.scss). Replaces bootstrap-vue's
 * <b-dropdown-item>. Renders a <button> by default, or an <a>/<router-link>
 * via `href` / `to`. Emits `click`; selecting an enabled item closes the menu
 * (coordinated through inject('playDropdown'), NOT $parent — Vue 3-safe).
 *
 * VUE3-MIGRATION: `click` is emitted explicitly (no `.native`), and
 * provide/inject + props/slots are already Vue 3-compatible.
 */
export default {
  name: 'PlayDropdownItem',
  inject: {
    playDropdown: { default: null },
  },
  inheritAttrs: false,
  props: {
    /** Render as a link with this href (<a>) */
    href: {
      type: String,
      default: '',
    },
    /** Render as <router-link> to this target */
    to: {
      type: [String, Object],
      default: null,
    },
    /** Highlight as the current selection */
    active: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Hover/active accent role */
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'secondary', 'highlight', 'accent', 'danger'].includes(v),
    },
  },
  computed: {
    resolvedTag() {
      if (this.to) return 'router-link'
      if (this.href) return 'a'
      return 'button'
    },
  },
  created() {
    if (this.playDropdown) this.playDropdown.register(this)
  },
  beforeDestroy() {
    if (this.playDropdown) this.playDropdown.unregister(this)
  },
  methods: {
    onClick(e) {
      if (this.disabled) {
        e.preventDefault()
        return
      }
      this.$emit('click', e)
      if (this.playDropdown) this.playDropdown.select()
    },
  },
}
</script>

<style lang="scss" scoped>
.play-dropdown-item {
  --it-on: var(--c-primary);
  --it-on-soft: var(--c-primary-soft);
  --it-on-strong: var(--c-primary-strong);
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  margin: 0;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 14px;
  text-align: left;
  text-decoration: none;
  color: var(--text-body);
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);

  & + & { margin-top: 2px; }

  &--secondary { --it-on: var(--c-secondary); --it-on-soft: var(--c-secondary-soft); --it-on-strong: var(--c-secondary-strong); }
  &--highlight { --it-on: var(--c-highlight); --it-on-soft: var(--c-highlight-soft); --it-on-strong: var(--c-highlight-strong); }
  &--accent    { --it-on: var(--c-accent);    --it-on-soft: var(--c-accent-soft);    --it-on-strong: var(--c-accent-strong); }
  &--danger    { --it-on: var(--c-danger);    --it-on-soft: var(--c-danger-soft);    --it-on-strong: var(--c-danger-strong); }

  &__icon {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    color: var(--it-on-strong);
  }

  &__label {
    flex: 1 1 auto;
    min-width: 0;
    line-height: 1.35;
  }

  /* ── States ── */
  &:hover:not(.is-disabled),
  &:focus-visible {
    background: var(--it-on-soft);
    color: var(--it-on-strong);
    outline: none;
  }

  &.is-active {
    background: var(--it-on-soft);
    color: var(--it-on-strong);
    font-weight: 600;
  }

  &.is-disabled {
    opacity: .5;
    cursor: not-allowed;
  }
}

@media (prefers-reduced-motion: reduce) {
  .play-dropdown-item { transition: none; }
}
</style>
